import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import { fetchBrandsAction } from "../../../redux/slices/categories/brandsSlice";
import { fetchCategoriesAction } from "../../../redux/slices/categories/categoriesSlice";
import { fetchStylesAction } from "../../../redux/slices/categories/stylesSlice";
import {
  fetchProductAction,
  updateProductAction,
} from "../../../redux/slices/products/productSlices";

import ErrorMsg from "../../ErrorMessage/ErrorMsg";
import LoadingComponent from "../../LoadingComp/LoadingComponent";
import SuccessMsg from "../../SuccessMsg/SuccessMsg";
import { resetSuccessAction } from "../../../redux/slices/globalActions/globalActions";

const animatedComponents = makeAnimated();

// Allowed sizes (we'll treat them as a list for the multi‑select)
const sizeOptionsList = ["S", "M", "L", "XL", "XXL"].map((s) => ({
  value: s,
  label: s,
}));

export default function ProductUpdate() {
  const dispatch = useDispatch();
  const { id } = useParams();

  // --- Fetch necessary lists & product ---
  useEffect(() => {
    dispatch(fetchProductAction(id));
    dispatch(fetchCategoriesAction());
    dispatch(fetchBrandsAction());
    dispatch(fetchStylesAction());
  }, [dispatch, id]);

  useEffect(() => {
  dispatch(resetSuccessAction());
}, [dispatch]);

  // --- Redux selectors with fallbacks ---
  const { product, isUpdated, loading, error } = useSelector(
    (state) => state?.products || {}
  );
  const actualProduct = product?.product; // adjust based on your slice shape

  const categories =
    useSelector((state) => state?.categories?.categories?.categories) || [];
  const brands =
    useSelector((state) => state?.brands?.brands?.brands) || [];
  const styles =
    useSelector((state) => state?.styles?.styles?.styles) || [];

  // --- State for multi‑select values ---
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [selectedStyles, setSelectedStyles] = useState([]);

  // --- Form data, updated when product loads ---
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "",
    brand: "",
    price: "",
    totalQty: "",
  });

  // When the product is fetched, pre‑fill the form & multi‑selects
  useEffect(() => {
    if (actualProduct) {
      setFormData({
        name: actualProduct.name || "",
        description: actualProduct.description || "",
        category: actualProduct.category?._id || actualProduct.category || "",
        brand: actualProduct.brand?._id || actualProduct.brand || "",
        price: actualProduct.price ?? "",
        totalQty: actualProduct.totalQty ?? "",
      });

      // Convert stored comma‑separated strings back to array for react‑select
      const sizeStr = actualProduct.sizes || "";
      const styleStr = actualProduct.style || "";
      setSelectedSizes(
        sizeStr
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
          .map((s) => ({ value: s, label: s }))
      );
      setSelectedStyles(
        styleStr
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
          .map((s) => ({ value: s, label: s }))
      );
    }
  }, [actualProduct]);

  // --- Handlers ---
  const handleOnChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleOnSubmit = (e) => {
    e.preventDefault();
    // Join arrays into strings
    const sizesStr = selectedSizes.map((opt) => opt.value).join(", ");
    const stylesStr = selectedStyles.map((opt) => opt.value).join(", ");

    dispatch(
      updateProductAction({
        name: formData.name,
        description: formData.description,
        category: formData.category,
        brand: formData.brand,
        price: Number(formData.price),
        totalQty: Number(formData.totalQty),
        sizes: sizesStr,
        style: stylesStr,
        id,
      })
    );

    // Keep the form as is (or optionally reset)
  };

  // Convert data for react‑select
  const categoryOptions = categories.map((cat) => ({
    value: cat._id,
    label: cat.name,
  }));
  const brandOptions = brands.map((br) => ({
    value: br._id,
    label: br.name,
  }));
  const styleOptions = styles.map((st) => ({
    value: st.name,
    label: st.name,
  }));

  // Find current option for category/brand single selects
  const selectedCategory = categoryOptions.find(
    (opt) => opt.value === formData.category
  );
  const selectedBrand = brandOptions.find(
    (opt) => opt.value === formData.brand
  );

  return (
    <>
      {error && <ErrorMsg message={error?.message} />}
      {isUpdated && <SuccessMsg message="Product Updated Successfully" />}
      <div className="flex min-h-full flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900">
            Update Product
          </h2>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <form className="space-y-6" onSubmit={handleOnSubmit}>
              {/* Product Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Product Name
                </label>
                <input
                  name="name"
                  value={formData.name}
                  onChange={handleOnChange}
                  className="block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
                />
              </div>

              {/* Size multi‑select (will join into string on submit) */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Sizes
                </label>
                <Select
                  isMulti
                  options={sizeOptionsList}
                  value={selectedSizes}
                  onChange={(items) => setSelectedSizes([...items])}
                  placeholder="Select sizes..."
                />
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Category
                </label>
                <Select
                  options={categoryOptions}
                  value={selectedCategory}
                  onChange={(opt) =>
                    setFormData({ ...formData, category: opt?.value || "" })
                  }
                  placeholder="Select category..."
                  isClearable
                />
              </div>

              {/* Brand */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Brand
                </label>
                <Select
                  options={brandOptions}
                  value={selectedBrand}
                  onChange={(opt) =>
                    setFormData({ ...formData, brand: opt?.value || "" })
                  }
                  placeholder="Select brand..."
                  isClearable
                />
              </div>

              {/* Style multi‑select */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Styles
                </label>
                <Select
                  isMulti
                  options={styleOptions}
                  value={selectedStyles}
                  onChange={(items) => setSelectedStyles([...items])}
                  placeholder="Select styles..."
                />
              </div>

              {/* Price */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Price
                </label>
                <input
                  name="price"
                  value={formData.price}
                  onChange={handleOnChange}
                  type="number"
                  className="block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
                />
              </div>

              {/* Total Quantity */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Total Quantity
                </label>
                <input
                  name="totalQty"
                  value={formData.totalQty}
                  onChange={handleOnChange}
                  type="number"
                  className="block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Description
                </label>
                <textarea
                  rows={4}
                  name="description"
                  value={formData.description}
                  onChange={handleOnChange}
                  className="block w-full rounded-md border border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                />
              </div>

              {/* Submit */}
              {loading ? (
                <LoadingComponent />
              ) : (
                <button
                  type="submit"
                  className="flex w-full justify-center rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                >
                  Update Product
                </button>
              )}
            </form>
          </div>
        </div>
      </div>
    </>
  );
}